import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';
import {
    Table,
    Tag,
    Button,
    Card,
    Space,
    Typography,
    Alert,
    Spin,
    Tooltip,
    Descriptions,
    Badge,
    message,
    Image,
    Modal,
    Input
} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    UserOutlined,
    ReloadOutlined,
    EyeOutlined,
    CloseOutlined,
    CheckOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from "dayjs";


const { Title, Text } = Typography;
const { TextArea } = Input;

export default function PastOnduty() {
    const [ondutyrequests, setOndutyrequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const { authData, backUrl } = useContext(AuthContext);

    const [isEmployeeView, setIsEmployeeView] = useState(
        localStorage.getItem("Hrselected") === "employee"
    );

    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    // Status filter state - default PENDING
    const [statusFilter, setStatusFilter] = useState('PENDING');

    // Counts for each status
    const [statusCounts, setStatusCounts] = useState({
        PENDING: 0,
        APPROVED: 0,
        REJECTED: 0,
    });

    // Search/filter state
    const [filters, setFilters] = useState({
        employee_name: '',
        employee_code: '',
        type: '',
        remarks: '',
        start_date: '',
        end_date: '',
    });

    // Selection state
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Modal states
    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [isBulkApproveModalVisible, setIsBulkApproveModalVisible] = useState(false);
    const [isBulkRejectModalVisible, setIsBulkRejectModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [approveComments, setApproveComments] = useState('');
    const [rejectComments, setRejectComments] = useState('');
    const [bulkApproveComments, setBulkApproveComments] = useState('');
    const [bulkRejectComments, setBulkRejectComments] = useState('');

    // Determine if current user is a regular employee (role === 2)
    const isRegularEmployee = authData?.employee?.role === 2;

    useEffect(() => {
        fetchOndutyrequests();
    }, [statusFilter, filters]);

    // Fetch counts for all statuses
    useEffect(() => {
        fetchAllStatusCounts();
    }, [filters]);

    /**
     * For regular employees (role === 2):
     *   Count is based on the record's own top-level `status` field.
     *
     * For managers/HR (role !== 2):
     *   PENDING count = records where logged-in employee is the current pending approver in approval_logs.
     *   APPROVED/REJECTED count = records where top-level status matches.
     */
    const fetchAllStatusCounts = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.employee_name) params.append('employee_name', filters.employee_name);
            if (filters.employee_code) params.append('employee_code', filters.employee_code);
            if (filters.type) params.append('type', filters.type);
            if (filters.remarks) params.append('remarks', filters.remarks);
            if (filters.start_date) params.append('from_date', filters.start_date);
            if (filters.end_date) params.append('to_date', filters.end_date);

            const statuses = ['PENDING', 'APPROVED', 'REJECTED'];

            const results = await Promise.all(
                statuses.map(async (status) => {
                    const p = new URLSearchParams(params);
                    p.append('status', status);
                    const apiUrl = isEmployeeView
                        ? `${backUrl}/api/hr/onduty/?is_employee_view=true&${p.toString()}`
                        : `${backUrl}/api/hr/onduty/?${p.toString()}`;
                    const res = await axios.get(apiUrl, {
                        headers: { Authorization: `Bearer ${authData?.access}` }
                    });

                    let data = Array.isArray(res.data) ? res.data : [];

                    if (isRegularEmployee) {
                        // For regular employees: filter purely by top-level status
                        data = data.filter(record => record.status === status);
                    } else {
                        // For managers/HR:
                        // PENDING = records where logged-in employee is the current pending approver
                        // APPROVED/REJECTED = records where top-level status matches
                        if (status === 'PENDING') {
                            data = data.filter(record => isCurrentApprover(record));
                        } else {
                            data = data.filter(record => record.status === status);
                        }
                    }

                    return { status, count: data.length };
                })
            );

            const counts = {};
            results.forEach(({ status, count }) => { counts[status] = count; });
            setStatusCounts(counts);
        } catch (err) {
            // silently fail — counts are non-critical
        }
    };

    /**
     * Checks if the logged-in employee is the current pending approver for a record.
     * Only relevant for managers/HR (role !== 2).
     */
    const isCurrentApprover = (record) => {
        const currentEmployeeId = authData?.employee?.id;
        if (!currentEmployeeId || !Array.isArray(record.approval_logs)) return false;

        return record.approval_logs.some(
            (log) => log.approver === currentEmployeeId && log.status === 'PENDING'
        );
    };

    const fetchOndutyrequests = async () => {
        try {
            setLoading(true);

            const params = new URLSearchParams();
            if (filters.employee_name) params.append('employee_name', filters.employee_name);
            if (filters.employee_code) params.append('employee_code', filters.employee_code);
            if (filters.type) params.append('type', filters.type);
            if (filters.remarks) params.append('remarks', filters.remarks);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            params.append('status', statusFilter);

            let apiUrl = isEmployeeView
                ? `${backUrl}/api/hr/onduty/?is_employee_view=true&${params.toString()}`
                : `${backUrl}/api/hr/onduty/?${params.toString()}`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });

            let data = Array.isArray(response.data) ? response.data : [];

            if (isRegularEmployee) {
                // For regular employees: show records by top-level status only
                data = data.filter(record => record.status === statusFilter);
            } else {
                // For managers/HR:
                // PENDING tab: only show records where logged-in employee is the current pending approver
                // APPROVED/REJECTED tabs: filter by top-level status
                if (statusFilter === 'PENDING') {
                    data = data.filter(record => isCurrentApprover(record));
                } else {
                    data = data.filter(record => record.status === statusFilter);
                }
            }

            setOndutyrequests(data);
            setError(null);
            setSelectedRowKeys([]);

        } catch (err) {
            setError(err.response?.data?.message || err.message);
            message.error('Failed to fetch onduty requests');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            employee_name: '',
            employee_code: '',
            type: '',
            remarks: '',
            start_date: '',
            end_date: '',
        });
    };

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: record.status !== 'PENDING',
        }),
    };

    const showApproveModal = (record) => {
        setSelectedRequest(record);
        setApproveComments('');
        setIsApproveModalVisible(true);
    };

    const showRejectModal = (record) => {
        setSelectedRequest(record);
        setRejectComments('');
        setIsRejectModalVisible(true);
    };

    const showBulkApproveModal = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one request');
            return;
        }
        setBulkApproveComments('');
        setIsBulkApproveModalVisible(true);
    };

    const showBulkRejectModal = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one request');
            return;
        }
        setBulkRejectComments('');
        setIsBulkRejectModalVisible(true);
    };

    const handleApproveOk = async () => {
        try {
            setProcessingId(selectedRequest.id);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "APPROVE",
                    "ids": [selectedRequest.id],
                    "comments": approveComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );

            message.success('Request approved successfully!');
            setIsApproveModalVisible(false);
            setApproveComments('');
            setSelectedRequest(null);
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error approving request: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessingId(null);
        }
    };

    const handleRejectOk = async () => {
        try {
            setProcessingId(selectedRequest.id);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "REJECT",
                    "ids": [selectedRequest.id],
                    "comments": rejectComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            message.success('Request rejected successfully!');
            setIsRejectModalVisible(false);
            setRejectComments('');
            setSelectedRequest(null);
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error rejecting request: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessingId(null);
        }
    };

    const handleBulkApproveOk = async () => {
        try {
            setLoading(true);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "APPROVE",
                    "ids": selectedRowKeys,
                    "comments": bulkApproveComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            message.success(`${selectedRowKeys.length} request(s) approved successfully!`);
            setIsBulkApproveModalVisible(false);
            setBulkApproveComments('');
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error approving requests: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBulkRejectOk = async () => {
        try {
            setLoading(true);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "REJECT",
                    "ids": selectedRowKeys,
                    "comments": bulkRejectComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            message.success(`${selectedRowKeys.length} request(s) rejected successfully!`);
            setIsBulkRejectModalVisible(false);
            setBulkRejectComments('');
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error rejecting requests: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleApproveCancel = () => {
        setIsApproveModalVisible(false);
        setApproveComments('');
        setSelectedRequest(null);
    };

    const handleRejectCancel = () => {
        setIsRejectModalVisible(false);
        setRejectComments('');
        setSelectedRequest(null);
    };

    const handleBulkApproveCancel = () => {
        setIsBulkApproveModalVisible(false);
        setBulkApproveComments('');
    };

    const handleBulkRejectCancel = () => {
        setIsBulkRejectModalVisible(false);
        setBulkRejectComments('');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const calculateDuration = (start, end) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        const diffMs = endTime - startTime;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${diffHrs}h ${diffMins}m`;
    };

    const getStatusColor = (status) => {
        const colors = {
            'APPROVED': 'success',
            'REJECTED': 'error',
            'PENDING': 'warning',
            'default': 'default'
        };
        return colors[status] || colors.default;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'APPROVED':
                return <CheckCircleOutlined />;
            case 'PENDING':
                return <ClockCircleOutlined />;
            default:
                return null;
        }
    };

    const approvalLogsColumns = [
        {
            title: 'Approver',
            dataIndex: 'approver_name',
            key: 'approver_name',
            width: 200,
            render: (name) => (
                <Space>
                    <Text strong>{name}</Text>
                </Space>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            align: 'center',
            render: (status) => (
                <p
                    color={getStatusColor(status)}
                    icon={getStatusIcon(status)}
                    style={{ minWidth: '90px', textAlign: 'center' }}
                >
                    {status}
                </p>
            )
        },
        {
            title: 'Comments',
            dataIndex: 'remarks',
            key: 'comments',
            width: 250,
            render: (comments) => (
                comments ? (
                    <Tooltip placement="topLeft" title={comments}>
                        <Text>{comments}</Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                )
            )
        },
        {
            title: 'Approved At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (time) => (
                <Text type="secondary">{formatDateTime(time)}</Text>
            )
        }
    ];

    const expandedRowRender = (record) => {
        return (
            <div style={{ padding: '16px 24px', backgroundColor: '#ffffff' }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Table
                            columns={approvalLogsColumns}
                            dataSource={record.approval_logs || []}
                            pagination={false}
                            rowKey={(log) => `${record.id}-${log.level_order}`}
                            size="small"
                            scroll={{ x: 600 }}
                            bordered
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th {...props} style={{
                                            backgroundColor: "#0e497b",
                                            color: 'white',
                                            fontSize: '13px'
                                        }}>
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                        />
                    </div>
                </Space>
            </div>
        );
    };

    const columns = [
        {
            title: 'Employee',
            key: 'employee',
            width: 100,
            render: (record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>
                        {record.employee_name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.employee_code}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Type',
            dataIndex: 'onduty_type',
            key: 'onduty_type',
            width: 80,
            render: (type, record) => (
                <>
                    <p>{type}</p>
                    <p>{record.check_in_out_mode}</p>
                    {record?.lead_data && (
                        <a
                            onClick={() => {
                                setSelectedLeadDetails(record.lead_data);
                                setShowLead(true);
                            }}
                            style={{ color: "#1677ff", cursor: "pointer" }}
                        >
                            Lead Details
                        </a>
                    )}
                </>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 100,
            render: (time) => (
                <Space>
                    <Text>{formatDateTime(time)}</Text>
                </Space>
            )
        },
        {
            title: 'End Time',
            dataIndex: 'end_time',
            key: 'end_time',
            width: 100,
            render: (time) => (
                <Space>
                    <Text>{formatDateTime(time)}</Text>
                </Space>
            )
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 120,
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            width: 270,
            render: (text, record) =>
                ((record.check_in_latitude && record.check_in_longitude) || (record.check_out_latitude && record.check_out_longitude)) ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude || record.check_out_latitude},${record.check_in_longitude || record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            align: 'center',
            render: (status) => (
                <Space direction="vertical" size={0} align="center">
                    <p>{status}</p>
                </Space>
            )
        },
        {
            title: 'Remarks',
            dataIndex: 'remarks',
            key: 'remarks',
            width: 200,
            ellipsis: {
                showTitle: false
            },
            render: (remarks) => (
                remarks ? (
                    <Tooltip placement="topLeft" title={remarks}>
                        <Text>{remarks}</Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                )
            )
        },
        {
            title: "Check-In Photo",
            dataIndex: "photo",
            key: "check_in_photo",
            width: 100,
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        // Only show Actions column for non-regular-employees (role !== 2)
        !isRegularEmployee && {
            title: 'Actions',
            key: 'actions',
            width: 150,
            align: 'center',
            render: (record) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => showApproveModal(record)}
                        loading={processingId === record.id}
                        shape="circle"
                        icon={<CheckOutlined />}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        disabled={record.status !== 'PENDING'}
                    />

                    <Button
                        danger
                        onClick={() => showRejectModal(record)}
                        loading={processingId === record.id}
                        shape="circle"
                        icon={<CloseOutlined />}
                        disabled={record.status !== 'PENDING'}
                    />
                </Space>
            )
        }
    ].filter(Boolean);

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error Loading Requests"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={fetchOndutyrequests}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }

    // Helper to render a status tab button with a count badge
    const StatusTabButton = ({ status, label, activeColor }) => {
        const isActive = statusFilter === status;
        const count = statusCounts[status] ?? 0;

        const getIcon = () => {
            if (status === "PENDING") return <ClockCircleOutlined />;
            if (status === "APPROVED") return <CheckCircleOutlined />;
            if (status === "REJECTED") return <CloseCircleOutlined />;
            return null;
        };

        return (
            <Badge
                count={count}
                style={{
                    backgroundColor: isActive ? activeColor : '#d9d9d9',
                    color: isActive ? '#fff' : '#595959',
                    fontWeight: 600,
                    boxShadow: 'none',
                }}
                overflowCount={999}
            >
                <Button
                    type={isActive ? "primary" : "default"}
                    onClick={() => setStatusFilter(status)}
                    icon={getIcon()}
                    style={{
                        marginRight: "10px",
                        backgroundColor: isActive ? activeColor : undefined,
                        borderColor: isActive ? activeColor : undefined,
                        minWidth: 120,
                    }}
                >
                    {label}
                </Button>
            </Badge>
        );
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#ffffff', minHeight: '100vh' }}>

            {showLead && (
                <Modal
                    open={showLead}
                    footer={null}
                    onCancel={() => {
                        setShowLead(false);
                        setSelectedLeadDetails(null);
                    }}
                    width={700}
                >
                    {selectedLeadDetails ? (
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                                <Descriptions.Item label="Lead Number">
                                    <strong>{selectedLeadDetails.lead_no || "-"}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    {selectedLeadDetails.customer_name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {selectedLeadDetails.email || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone Number">
                                    {selectedLeadDetails.number || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Project Name">
                                    {selectedLeadDetails.project_name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span
                                        style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: selectedLeadDetails.status
                                                ?.toLowerCase()
                                                .includes("cancel")
                                                ? "#ffffff"
                                                : "#ffffff",
                                            color: selectedLeadDetails.status?.toLowerCase().includes("cancel")
                                                ? "#d46b08"
                                                : "#52c41a",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {selectedLeadDetails.status || "-"}
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                            No lead details available
                        </div>
                    )}
                </Modal>
            )}

            <Space direction="vertical" size="large" style={{ width: '100%' }}>

                {/* Status Filter Buttons with Counts */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: "20px", gap: '28px' }}>
                    <StatusTabButton status="PENDING" label="Pending" activeColor="#faad14" />
                    <StatusTabButton status="APPROVED" label="Approved" activeColor="#4CAF50" />
                    <StatusTabButton status="REJECTED" label="Rejected" activeColor="#f5222d" />
                </div>

                {/* Search Filters - Centered */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '16px',
                }}>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        alignItems: 'center',
                        padding: '16px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8',
                        justifyContent: 'center',
                    }}>
                        <Input
                            placeholder="Employee Name"
                            value={filters.employee_name}
                            onChange={(e) => handleFilterChange('employee_name', e.target.value)}
                            allowClear
                            style={{ width: 160 }}
                        />
                        <Input
                            placeholder="Employee Code"
                            value={filters.employee_code}
                            onChange={(e) => handleFilterChange('employee_code', e.target.value)}
                            allowClear
                            style={{ width: 140 }}
                        />
                        <Input
                            placeholder="Type"
                            value={filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            allowClear
                            style={{ width: 120 }}
                        />
                        <Input
                            placeholder="Remarks"
                            value={filters.remarks}
                            onChange={(e) => handleFilterChange('remarks', e.target.value)}
                            allowClear
                            style={{ width: 140 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>From:</Text>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.start_date}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                style={{ width: 145 }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>To:</Text>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.end_date}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                style={{ width: 145 }}
                            />
                        </div>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={clearFilters}
                        >
                            Clear
                        </Button>
                    </div>
                </div>


                {/* Bulk Action Buttons — only for non-regular-employees */}
                {!isRegularEmployee && statusFilter === 'PENDING' && selectedRowKeys.length !== 0 && (
                    <div
                        style={{
                            marginBottom: '16px',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center'
                        }}
                    >
                        <Button
                            type="primary"
                            onClick={showBulkApproveModal}
                            disabled={selectedRowKeys.length === 0}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Bulk Approve
                        </Button>

                        <Button
                            danger
                            onClick={showBulkRejectModal}
                            disabled={selectedRowKeys.length === 0}
                        >
                            Bulk Reject
                        </Button>
                    </div>
                )}

                {/* Spinner overlay wrapping the Table */}
                <Spin
                    spinning={loading}
                    size="large"
                    tip="Loading..."
                    style={{ width: '100%' }}
                >
                    <Table
                        columns={columns}
                        dataSource={ondutyrequests}
                        rowKey="id"
                        loading={false}
                        // Row selection only for non-regular-employees on PENDING tab
                        rowSelection={!isRegularEmployee && statusFilter === 'PENDING' ? rowSelection : null}
                        expandable={{
                            expandedRowRender,
                            expandIcon: ({ expanded, onExpand, record }) => (
                                <Button
                                    type="link"
                                    icon={<EyeOutlined />}
                                    onClick={(e) => onExpand(record, e)}
                                    size="small"
                                    style={{ color: '#0e497b' }}
                                />
                            ),
                            expandRowByClick: false,
                        }}
                        pagination={{
                            pageSize: 20,
                            showTotal: (total) => `Total ${total} requests`,
                        }}
                        scroll={{ x: 1700 }}
                        locale={{
                            emptyText: (
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: '16px' }}>
                                        No onduty requests found
                                    </Text>
                                </div>
                            )
                        }}
                        components={{
                            header: {
                                cell: (props) => (
                                    <th {...props} style={{
                                        backgroundColor: "#0e497b",
                                        color: 'white'
                                    }}>
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                        bordered
                    />
                </Spin>
            </Space>

            {/* Approve Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                        <span>Approve Request</span>
                    </div>
                }
                open={isApproveModalVisible}
                onOk={handleApproveOk}
                onCancel={handleApproveCancel}
                okText="Approve"
                cancelText="Cancel"
                confirmLoading={processingId === selectedRequest?.id}
                okButtonProps={{
                    style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                }}
            >
                {selectedRequest && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Text strong>Employee: </Text>
                            <Text>{selectedRequest.employee_name} ({selectedRequest.employee_code})</Text>
                        </div>
                        <div>
                            <Text strong>Type: </Text>
                            <Text>{selectedRequest.onduty_type}</Text>
                        </div>
                        {(selectedRequest.start_time && selectedRequest.end_time) && (
                            <div>
                                <Text strong>Duration: </Text>
                                <Text>{calculateDuration(selectedRequest.start_time, selectedRequest.end_time)}</Text>
                            </div>
                        )}
                        <div>
                            <Text strong style={{ color: '#ff4d4f' }}>Comments: *</Text>
                            <TextArea
                                rows={4}
                                placeholder="Enter approval comments..."
                                value={approveComments}
                                onChange={(e) => setApproveComments(e.target.value)}
                                maxLength={500}
                                showCount
                                style={{ marginTop: '8px' }}
                            />
                        </div>
                    </Space>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CloseOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                        <span>Reject Request</span>
                    </div>
                }
                open={isRejectModalVisible}
                onOk={handleRejectOk}
                onCancel={handleRejectCancel}
                okText="Reject"
                cancelText="Cancel"
                confirmLoading={processingId === selectedRequest?.id}
                okButtonProps={{ danger: true }}
            >
                {selectedRequest && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Text strong>Employee: </Text>
                            <Text>{selectedRequest.employee_name} ({selectedRequest.employee_code})</Text>
                        </div>
                        <div>
                            <Text strong>Type: </Text>
                            <Text>{selectedRequest.onduty_type}</Text>
                        </div>
                        {(selectedRequest.start_time && selectedRequest.end_time) && (
                            <div>
                                <Text strong>Duration: </Text>
                                <Text>{calculateDuration(selectedRequest.start_time, selectedRequest.end_time)}</Text>
                            </div>
                        )}
                        <div>
                            <Text strong style={{ color: '#ff4d4f' }}>Reason for Rejection: *</Text>
                            <TextArea
                                rows={4}
                                placeholder="Enter reason for rejection..."
                                value={rejectComments}
                                onChange={(e) => setRejectComments(e.target.value)}
                                maxLength={500}
                                showCount
                                style={{ marginTop: '8px' }}
                            />
                        </div>
                    </Space>
                )}
            </Modal>

            {/* Bulk Approve Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Approve Requests</span>
                    </div>
                }
                open={isBulkApproveModalVisible}
                onOk={handleBulkApproveOk}
                onCancel={handleBulkApproveCancel}
                okText="Approve All"
                cancelText="Cancel"
                confirmLoading={loading}
                okButtonProps={{
                    style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                }}
                width={600}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message={`You are about to approve ${selectedRowKeys.length} request(s)`}
                        type="info"
                        showIcon
                    />
                    <div>
                        <Text strong style={{ color: '#ff4d4f' }}>Comments (will be applied to all): *</Text>
                        <TextArea
                            rows={4}
                            placeholder="Enter approval comments for all selected requests..."
                            value={bulkApproveComments}
                            onChange={(e) => setBulkApproveComments(e.target.value)}
                            maxLength={500}
                            showCount
                            style={{ marginTop: '8px' }}
                        />
                    </div>
                </Space>
            </Modal>

            {/* Bulk Reject Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Reject Requests</span>
                    </div>
                }
                open={isBulkRejectModalVisible}
                onOk={handleBulkRejectOk}
                onCancel={handleBulkRejectCancel}
                okText="Reject All"
                cancelText="Cancel"
                confirmLoading={loading}
                okButtonProps={{ danger: true }}
                width={600}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message={`You are about to reject ${selectedRowKeys.length} request(s)`}
                        type="warning"
                        showIcon
                    />
                    <div>
                        <Text strong style={{ color: '#ff4d4f' }}>Reason for Rejection (will be applied to all): *</Text>
                        <TextArea
                            rows={4}
                            placeholder="Enter reason for rejection for all selected requests..."
                            value={bulkRejectComments}
                            onChange={(e) => setBulkRejectComments(e.target.value)}
                            maxLength={500}
                            showCount
                            style={{ marginTop: '8px' }}
                        />
                    </div>
                </Space>
            </Modal>
        </div>
    );
}
